import {
  OktonPageHeader,
  OktonConfirmDialog,
  OktonMetricCard,
  OktonDateFilter,
  OktonLoadingCard,
  OktonErrorCard,
  OktonSearchSelect,
  OktonSearchFilter,
  ColunaGrid,
  useGrid,
  OktonDataGrid,
  TAMANHOS_PAGINA,
} from '@okton/platform-ui';
import {
  useToast,
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  IconButton,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  ArrowDownLeft,
  ArrowUpRight,
  Landmark,
  Plus,
  Trash2,
  Wallet,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import BankAppPage from '../../layout/BankAppPage';
import { useContasBancarias } from '../../services/hooks/useContasBancarias';
import {
  Deposito,
  STATUS_DEPOSITO,
  useDepositos,
} from '../../services/hooks/useDepositos';
import {
  LancamentoFinanceiro,
  TIPO_FIN_RECEBER,
  useLancamentosFinanceiros,
} from '../../services/hooks/useLancamentosFinanceiros';
import { useDeleteLancamentoFinanceiro } from '../../services/hooks/useLancamentosFinanceirosMutations';
import { useResumoMonitorBancario } from '../../services/hooks/useMonitorBancario';
import { Saque, useSaques } from '../../services/hooks/useSaques';
import {
  pontaDaConta,
  Transferencia,
  useTransferencias,
} from '../../services/hooks/useTransferencias';
import { formatCustomDate } from '../../utils/formatCustomDate';
import formatValue from '../../utils/formatValue';
import { FiltroData, setaFiltroAnual } from '../../utils/setaFiltroDiario';
import GridMovimentos from './GridMovimentos';
import { colunasDataGrid } from '../../utils/colunasDataGrid';

const valorAssinado = (l: LancamentoFinanceiro) =>
  (l.tipo_fin === TIPO_FIN_RECEBER ? 1 : -1) * Number(l.valorbaixa_fin);

const COLUNAS: ColunaGrid<LancamentoFinanceiro>[] = [
  { chave: 'baixa_fin', titulo: 'Data', valor: l => l.baixa_fin, tipo: 'data' },
  {
    chave: 'observacao_fin',
    titulo: 'Observação',
    valor: l => l.observacao_fin,
  },
  { chave: 'nome_cdc', titulo: 'Centro de Custo', valor: l => l.nome_cdc },
  { chave: 'nome_cli', titulo: 'Cliente', valor: l => l.nome_cli },
  { chave: 'documento_fin', titulo: 'Referência', valor: l => l.documento_fin },
  { chave: 'nome_moe', titulo: 'Moeda Banco', valor: l => l.nome_moe },
  {
    chave: 'valorbaixa_fin',
    titulo: 'Valor',
    valor: valorAssinado,
    tipo: 'numero',
    alinhamento: 'right',
  },
  {
    chave: 'conciliado_fin',
    titulo: 'Status',
    valor: l => (l.conciliado_fin === '1' ? 'Conciliado' : 'Pendente'),
    alinhamento: 'center',
  },
];

const COLUNAS_SAQUES: ColunaGrid<Saque>[] = [
  { chave: 'data_saq', titulo: 'Data', valor: s => s.data_saq, tipo: 'data' },
  {
    chave: 'observacao_saq',
    titulo: 'Observação',
    valor: s => s.observacao_saq,
  },
  {
    chave: 'nomemoedabanco_saq',
    titulo: 'Moeda Banco',
    valor: s => s.nomemoedabanco_saq,
  },
  { chave: 'nome_pct', titulo: 'Plano de Contas', valor: s => s.nome_pct },
  {
    chave: 'valor_saq',
    titulo: 'Valor',
    valor: s => -Number(s.valor_saq),
    tipo: 'numero',
    alinhamento: 'right',
  },
  {
    chave: 'conciliado_saq',
    titulo: 'Status',
    valor: s => (s.conciliado_saq !== '0' ? 'Conciliado' : 'Pendente'),
    alinhamento: 'center',
  },
];

const COLUNAS_DEPOSITOS: ColunaGrid<Deposito>[] = [
  { chave: 'data_dep', titulo: 'Data', valor: d => d.data_dep, tipo: 'data' },
  {
    chave: 'observacao_dep',
    titulo: 'Observação',
    valor: d => d.observacao_dep,
  },
  { chave: 'nome_moe', titulo: 'Moeda Depositada', valor: d => d.nome_moe },
  { chave: 'envelope_dep', titulo: 'Envelope', valor: d => d.envelope_dep },
  {
    chave: 'status_dep',
    titulo: 'Situação',
    valor: d => STATUS_DEPOSITO[d.status_dep] ?? d.status_dep,
  },
  {
    chave: 'valor_dep',
    titulo: 'Valor',
    valor: d => Number(d.valor_dep),
    tipo: 'numero',
    alinhamento: 'right',
  },
  {
    chave: 'conciliado_dep',
    titulo: 'Status',
    valor: d => (d.conciliado_dep !== '0' ? 'Conciliado' : 'Pendente'),
    alinhamento: 'center',
  },
];

const dataFormatada = (data: string) =>
  formatCustomDate(data.slice(0, 10), 'yyyy-MM-dd', 'dd/MM/yyyy');

const statusConciliacao = (conciliado: boolean) => (
  <Badge
    colorScheme={conciliado ? 'green' : 'orange'}
    borderRadius="full"
    px="2"
  >
    {conciliado ? 'Conciliado' : 'Pendente'}
  </Badge>
);

const valorTransferencia = (t: Transferencia, conta: string) =>
  (pontaDaConta(t, conta)?.saida ? -1 : 1) * Number(t.valor_tba);

const contraparteTransferencia = (t: Transferencia, conta: string) =>
  pontaDaConta(t, conta)?.saida
    ? t.destinodescricao_tba ?? t.destinomoeda_tba
    : t.origemdescricao_tba ?? t.origemmoeda_tba;

const MonitorBancario = () => {
  const history = useHistory();
  const { state } = useLocation<
    { conta?: string; periodo?: FiltroData } | undefined
  >();
  const bg = useColorModeValue('white', 'gray.800');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');
  const [periodo, setPeriodo] = useState<FiltroData>(
    state?.periodo ?? setaFiltroAnual(),
  );
  const [contaEscolhida, setContaEscolhida] = useState(state?.conta ?? '');
  const [busca, setBusca] = useState('');
  const toast = useToast();
  const [itemExcluir, setItemExcluir] = useState<string | null>(null);
  const [excluindo, setExcluindo] = useState<string | null>(null);

  const contas = useContasBancarias();
  // Ao abrir, usa a primeira conta; se ela for removida, volta para uma conta
  // existente sem consultar lançamentos de todas as contas por acidente.
  const conta = contas.data?.some(c => c.id_cts === contaEscolhida)
    ? contaEscolhida
    : contas.data?.[0]?.id_cts ?? '';
  const periodoValido =
    !!periodo[1] && !!periodo[2] && periodo[1] <= periodo[2];
  const habilitado = !!conta && periodoValido;
  const resumo = useResumoMonitorBancario(
    conta,
    periodo[1],
    periodo[2],
    periodoValido,
  );
  const lancamentos = useLancamentosFinanceiros(
    conta,
    'all',
    periodo[1],
    periodo[2],
    'banco',
    habilitado,
  );
  const saques = useSaques(conta, periodo[1], periodo[2], 'all', habilitado);
  const depositos = useDepositos(
    conta,
    periodo[1],
    periodo[2],
    'all',
    habilitado,
  );
  const transferencias = useTransferencias(
    conta,
    'all',
    periodo[1],
    periodo[2],
    habilitado,
  );
  const colunasTransferencias = useMemo<ColunaGrid<Transferencia>[]>(
    () => [
      {
        chave: 'data_tba',
        titulo: 'Data',
        valor: t => t.data_tba,
        tipo: 'data',
      },
      {
        chave: 'documento_tba',
        titulo: 'Referência',
        valor: t => t.documento_tba,
      },
      {
        chave: 'contraparte',
        titulo: 'Contraparte',
        valor: t => contraparteTransferencia(t, conta),
      },
      {
        chave: 'observacao_tba',
        titulo: 'Observação',
        valor: t => t.observacao_tba,
      },
      {
        chave: 'sentido',
        titulo: 'Tipo',
        valor: t => (pontaDaConta(t, conta)?.saida ? 'Saída' : 'Entrada'),
      },
      {
        chave: 'valor_tba',
        titulo: 'Valor',
        valor: t => valorTransferencia(t, conta),
        tipo: 'numero',
        alinhamento: 'right',
      },
      {
        chave: 'status',
        titulo: 'Status',
        valor: t =>
          pontaDaConta(t, conta)?.conciliada ? 'Conciliado' : 'Pendente',
        alinhamento: 'center',
      },
    ],
    [conta],
  );
  const transferenciasDaConta = (transferencias.data ?? []).filter(t =>
    pontaDaConta(t, conta),
  );
  const excluir = useDeleteLancamentoFinanceiro();
  const {
    ordenacao,
    alternarOrdenacao,
    itens: visiveis,
    filtrados,
    pagina,
    setPagina,
    tamanhoPagina,
    setTamanhoPagina,
  } = useGrid(lancamentos.data ?? [], COLUNAS, busca);

  const remover = async () => {
    if (!itemExcluir) return;
    setExcluindo(itemExcluir);
    try {
      await excluir.mutateAsync(itemExcluir);
      setItemExcluir(null);
    } catch (err) {
      toast({
        title:
          err instanceof Error ? err.message : 'Erro ao excluir o lançamento.',
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    } finally {
      setExcluindo(null);
    }
  };

  const recarregar = () => {
    contas.refetch();
    resumo.refetch();
    lancamentos.refetch();
  };
  const criar = (caminho: string) =>
    history.push(caminho, { conta, periodo, voltarPara: '/monitor-bancario' });
  const carregando = contas.isLoading || resumo.isLoading;
  const erro = contas.isError || resumo.isError;
  const carregandoLancamentos = contas.isLoading || lancamentos.isLoading;
  const erroLancamentos = contas.isError || lancamentos.isError;

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="GERAL"
        title="Monitor Bancário"
        subtitle="Movimentos internos da conta, conciliados ou pendentes"
        actions={
          <OktonDateFilter
            value={{ preset: periodo[0], start: periodo[1], end: periodo[2] }}
            onChange={next => setPeriodo([next.preset, next.start, next.end])}
          />
        }
      />

      <Flex mt="4" align="center" flexWrap="wrap" gridGap="3">
        <Flex
          borderWidth="1px"
          p="2"
          rounded="2xl"
          bg={bg}
          align="center"
          gridGap="2"
        >
          <Text px="2" fontSize="sm" fontWeight="medium">
            Conta bancária
          </Text>
          <OktonSearchSelect
            name="monitor-conta"
            value={conta}
            onChange={setContaEscolhida}
            placeholder="Selecione uma conta"
            minWidth="260px"
            options={(contas.data ?? []).map(c => ({
              value: c.id_cts,
              label: c.descricao_cts,
            }))}
          />
        </Flex>
        <OktonSearchFilter
          value={busca}
          onChange={setBusca}
          placeholder="Buscar em todos os movimentos..."
        />
      </Flex>

      {!periodoValido && (
        <Text mt="3" color="red.500">
          Informe um período válido, com a data final igual ou posterior à
          inicial.
        </Text>
      )}

      <Grid
        mt="5"
        templateColumns={[
          '1fr',
          'repeat(2, 1fr)',
          null,
          null,
          'repeat(4, 1fr)',
        ]}
        gap="5"
      >
        <OktonMetricCard
          label="Saldo Anterior"
          value={
            conta && !erro && periodoValido
              ? formatValue(resumo.data?.saldoanterior ?? 0)
              : '—'
          }
          icon={Landmark}
          accentColor="blue.500"
          isLoading={carregando}
        />
        <OktonMetricCard
          label="Total de Entradas"
          value={
            conta && !erro && periodoValido
              ? formatValue(resumo.data?.entradas ?? 0)
              : '—'
          }
          icon={ArrowDownLeft}
          accentColor="green.500"
          isLoading={carregando}
        />
        <OktonMetricCard
          label="Total de Saídas"
          value={
            conta && !erro && periodoValido
              ? formatValue(resumo.data?.saidas ?? 0)
              : '—'
          }
          icon={ArrowUpRight}
          accentColor="red.500"
          isLoading={carregando}
        />
        <OktonMetricCard
          label="Saldo Disponível"
          value={
            conta && !erro && periodoValido
              ? formatValue(resumo.data?.saldodisponivel ?? 0)
              : '—'
          }
          icon={Wallet}
          accentColor="teal.500"
          isLoading={carregando}
        />
      </Grid>
      {erro && <OktonErrorCard height="120px" onRetry={recarregar} />}

      <Box mt="6" bg={bg} borderWidth="1px" rounded="2xl" overflowX="auto">
        <Flex px="5" py="4" align="center" justify="space-between">
          <Heading fontSize="lg" fontWeight="medium">
            Lançamentos do período
          </Heading>
          <Button
            size="sm"
            leftIcon={<Plus size={16} />}
            colorScheme="blue"
            variant="outline"
            isDisabled={!habilitado}
            onClick={() => criar('/lancamentos/novo')}
          >
            Novo Lançamento
          </Button>
        </Flex>
        {carregandoLancamentos ? (
          <OktonLoadingCard height="200px" />
        ) : erroLancamentos ? (
          <OktonErrorCard height="200px" onRetry={recarregar} />
        ) : !conta ? (
          <Flex py="16" justify="center" color="gray.500">
            Nenhuma conta bancária disponível
          </Flex>
        ) : !periodoValido ? (
          <Flex py="16" justify="center" color="gray.500">
            Selecione um período válido
          </Flex>
        ) : filtrados.length === 0 ? (
          <Flex py="16" justify="center" color="gray.500">
            {busca
              ? 'Nada encontrado para a busca'
              : 'Nenhum lançamento no período'}
          </Flex>
        ) : (
          <Box px="5" pb="5">
            <OktonDataGrid
              rows={visiveis}
              columns={[
                ...colunasDataGrid(COLUNAS, ordenacao, alternarOrdenacao, {
                  baixa_fin: l => (
                    <Box whiteSpace="nowrap" color="gray.600">
                      {formatCustomDate(
                        l.baixa_fin.slice(0, 10),
                        'yyyy-MM-dd',
                        'dd/MM/yyyy',
                      )}
                    </Box>
                  ),
                  observacao_fin: l => (
                    <Box
                      color={color}
                      fontWeight="bold"
                      maxW="220px"
                      isTruncated
                    >
                      {l.observacao_fin || '—'}
                    </Box>
                  ),
                  nome_cdc: l => <>{l.nome_cdc || '—'}</>,
                  nome_cli: l => (
                    <Box color="gray.500">{l.nome_cli || '—'}</Box>
                  ),
                  documento_fin: l => (
                    <Box color="gray.500">{l.documento_fin || '—'}</Box>
                  ),
                  nome_moe: l => (
                    <Box color="gray.500">{l.nome_moe || '—'}</Box>
                  ),
                  valorbaixa_fin: l => {
                    const valor = valorAssinado(l);
                    return (
                      <Box
                        fontWeight="semibold"
                        whiteSpace="nowrap"
                        color={valor >= 0 ? 'green.600' : 'red.500'}
                      >
                        {formatValue(valor)}
                      </Box>
                    );
                  },
                  conciliado_fin: l => {
                    const conciliado = l.conciliado_fin === '1';
                    return (
                      <>
                        <Badge
                          colorScheme={conciliado ? 'green' : 'orange'}
                          borderRadius="full"
                          px="2"
                        >
                          {conciliado ? 'Conciliado' : 'Pendente'}
                        </Badge>
                      </>
                    );
                  },
                }),
                {
                  key: 'acoes',
                  header: null,
                  align: 'right',
                  render: l => {
                    const conciliado = l.conciliado_fin === '1';
                    return (
                      <Flex justify="flex-end" gridGap="1">
                        {!conciliado && (
                          <IconButton
                            aria-label="Excluir lançamento"
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            icon={<Trash2 size={16} />}
                            isDisabled={excluir.isLoading}
                            onClick={() => setItemExcluir(l.id_fin)}
                          />
                        )}
                      </Flex>
                    );
                  },
                },
              ]}
              getRowId={l => l.id_fin}
              page={pagina + 1}
              pageSize={tamanhoPagina}
              total={filtrados.length}
              pageSizeOptions={TAMANHOS_PAGINA}
              onPageChange={nova => setPagina(nova - 1)}
              onPageSizeChange={setTamanhoPagina}
            />
          </Box>
        )}
      </Box>
      <GridMovimentos
        titulo="Saques"
        novo="Novo Saque"
        onNovo={() => criar('/saques/novo')}
        rows={saques.data ?? []}
        colunas={COLUNAS_SAQUES}
        busca={busca}
        loading={contas.isLoading || saques.isLoading}
        error={contas.isError || saques.isError}
        onRetry={() => saques.refetch()}
        habilitado={habilitado}
        vazio="Nenhum saque no período"
        chave={s => s.id_saq}
        celulas={{
          data_saq: s => (
            <Box whiteSpace="nowrap">{dataFormatada(s.data_saq)}</Box>
          ),
          observacao_saq: s => s.observacao_saq || '—',
          nomemoedabanco_saq: s => (
            <Box color="gray.500">{s.nomemoedabanco_saq || '—'}</Box>
          ),
          nome_pct: s => <Box color="gray.500">{s.nome_pct || '—'}</Box>,
          valor_saq: s => (
            <Box color="red.500" fontWeight="semibold" whiteSpace="nowrap">
              {formatValue(-Number(s.valor_saq))}
            </Box>
          ),
          conciliado_saq: s => statusConciliacao(s.conciliado_saq !== '0'),
        }}
      />
      <GridMovimentos
        titulo="Depósitos"
        novo="Novo Depósito"
        onNovo={() => criar('/depositos/novo')}
        rows={depositos.data ?? []}
        colunas={COLUNAS_DEPOSITOS}
        busca={busca}
        loading={contas.isLoading || depositos.isLoading}
        error={contas.isError || depositos.isError}
        onRetry={() => depositos.refetch()}
        habilitado={habilitado}
        vazio="Nenhum depósito no período"
        chave={d => d.id_dep}
        celulas={{
          data_dep: d => (
            <Box whiteSpace="nowrap">{dataFormatada(d.data_dep)}</Box>
          ),
          observacao_dep: d => d.observacao_dep || '—',
          nome_moe: d => <Box color="gray.500">{d.nome_moe || '—'}</Box>,
          envelope_dep: d => (
            <Box color="gray.500">{d.envelope_dep || '—'}</Box>
          ),
          status_dep: d => (
            <Badge
              colorScheme={d.status_dep === '1' ? 'orange' : 'green'}
              borderRadius="full"
              px="2"
            >
              {STATUS_DEPOSITO[d.status_dep] ?? d.status_dep}
            </Badge>
          ),
          valor_dep: d => (
            <Box color="green.600" fontWeight="semibold" whiteSpace="nowrap">
              {formatValue(d.valor_dep)}
            </Box>
          ),
          conciliado_dep: d => statusConciliacao(d.conciliado_dep !== '0'),
        }}
      />
      <GridMovimentos
        titulo="Transferências"
        novo="Nova Transferência"
        onNovo={() => criar('/transferencias/nova')}
        rows={transferenciasDaConta}
        colunas={colunasTransferencias}
        busca={busca}
        loading={contas.isLoading || transferencias.isLoading}
        error={contas.isError || transferencias.isError}
        onRetry={() => transferencias.refetch()}
        habilitado={habilitado}
        vazio="Nenhuma transferência no período"
        chave={t => t.id_tba}
        celulas={{
          data_tba: t => (
            <Box whiteSpace="nowrap">{dataFormatada(t.data_tba)}</Box>
          ),
          documento_tba: t => t.documento_tba || '—',
          contraparte: t => (
            <Box color="gray.500">
              {contraparteTransferencia(t, conta) || '—'}
            </Box>
          ),
          observacao_tba: t => t.observacao_tba || '—',
          sentido: t => (pontaDaConta(t, conta)?.saida ? 'Saída' : 'Entrada'),
          valor_tba: t => {
            const valor = valorTransferencia(t, conta);
            return (
              <Box
                color={valor >= 0 ? 'green.600' : 'red.500'}
                fontWeight="semibold"
                whiteSpace="nowrap"
              >
                {formatValue(valor)}
              </Box>
            );
          },
          status: t => statusConciliacao(!!pontaDaConta(t, conta)?.conciliada),
        }}
      />
      <Box mb="10" />
      <OktonConfirmDialog
        isOpen={Boolean(itemExcluir)}
        onClose={() => {
          if (!excluindo) setItemExcluir(null);
        }}
        onConfirm={remover}
        title="Confirmar exclusão"
        description="Excluir este lançamento do IFLEX?"
        confirmLabel="Excluir"
        intent="danger"
        isLoading={Boolean(excluindo)}
      />
    </BankAppPage>
  );
};

export default MonitorBancario;
